/**
 * A named list's frame (#337): the name, checklist/plain switch, progress and
 * menu above the items, and "Add item" below them. The items themselves are
 * editable content (NamedListItemView).
 */
import { useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from '@tiptap/react';
import { ListChecks, List, MoreHorizontal, Plus } from 'lucide-react';
import type { NamedListOptions } from './NamedListNodes';
import { NamedListMenu } from './NamedListMenu';
import { useNamedListMenu } from './useNamedListMenu';

export function NamedListView({ node, editor, extension }: NodeViewProps) {
  const { listId, name, kind } = node.attrs as { listId: string; name: string; kind: 'checklist' | 'plain' };
  const opts = extension.options as NamedListOptions;
  const [draft, setDraft] = useState(name);
  const menu = useNamedListMenu(editor);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setDraft(name); }, [name]);

  let done = 0;
  let total = 0;
  node.forEach((c) => { total++; if (c.attrs.checked) done++; });

  const rename = () => { if (draft.trim() !== name) editor.commands.renameNamedList(listId, draft); };
  const run = (fn: () => void) => () => { menu.close(); fn(); };

  return (
    <NodeViewWrapper className="named-list" data-kind={kind}>
      <div className="named-list-header" contentEditable={false}>
        <input
          aria-label="List name"
          className="named-list-name"
          value={draft}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={rename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); rename(); editor.commands.focus(); }
            if (e.key === 'Escape') { setDraft(name); editor.commands.focus(); }
          }}
        />
        {kind === 'checklist' && total > 0 && <span className="named-list-progress">{done}/{total}</span>}
        <button
          type="button"
          className="named-list-btn"
          aria-label={kind === 'checklist' ? 'Make a plain list' : 'Make a checklist'}
          title={kind === 'checklist' ? 'Make a plain list' : 'Make a checklist'}
          onClick={() => editor.commands.setNamedListKind(listId, kind === 'checklist' ? 'plain' : 'checklist')}
        >
          {kind === 'checklist' ? <ListChecks size={14} /> : <List size={14} />}
        </button>
        <button ref={triggerRef} type="button" className="named-list-btn" aria-label="List menu" title="List menu" aria-haspopup="menu" aria-expanded={menu.open} onClick={menu.toggle}>
          <MoreHorizontal size={14} />
        </button>
        {/* #358: portaled with a backdrop, so a tap outside cannot land on the editor underneath. */}
        {menu.open && (
          <NamedListMenu anchor={triggerRef} label="List menu" onDismiss={menu.dismiss}>
            {opts.onTurnOpenItemsIntoTasks && (
              <button type="button" role="menuitem" onClick={run(() => opts.onTurnOpenItemsIntoTasks!(listId))}>Turn open items into tasks</button>
            )}
            {opts.onBringForward && (
              <button type="button" role="menuitem" onClick={run(() => opts.onBringForward!(listId))}>Bring forward open items</button>
            )}
            <button type="button" role="menuitem" className="danger" onClick={run(() => { editor.commands.removeNamedList(listId); editor.commands.focus(); })}>Delete list</button>
          </NamedListMenu>
        )}
      </div>
      <NodeViewContent className="named-list-items" />
      <button type="button" contentEditable={false} className="named-list-add" aria-label="Add item" onClick={() => editor.commands.addListItem(listId)}>
        <Plus size={14} /> Add item
      </button>
    </NodeViewWrapper>
  );
}
